// TurfAI Document Management Service

import { 
  Collection, 
  Document, 
  DocumentType, 
  EmployeeInfo,
  CreateCollectionData,
  StrapiResponse,
  StrapiError,
} from './types';
import { turfaiConfig } from './config';
import { authService } from './authService';

export class TurfAIDMSService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = turfaiConfig.dms.apiUrl;
  }

  // Collection Management
  async createOnboardingCollection(employeeData: EmployeeInfo): Promise<Collection> {
    try {
      const collectionData: CreateCollectionData = {
        name: `Employee_Onboarding_${employeeData.employee_id}`,
        description: `Onboarding documents for ${employeeData.name || employeeData.employee_id}`,
        metadata: {
          ...employeeData,
          status: 'pending',
          created_at: new Date().toISOString(),
        }
      };

      const response = await fetch(`${this.baseUrl}/collections/create-collection`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ data: collectionData })
      });

      if (!response.ok) {
        const error = await response.json() as StrapiError;
        throw new Error(error.error?.message || `Failed to create collection: ${response.statusText}`);
      }

      const collection = await response.json();
      
      // Transform to expected Collection format
      return {
        id: collection.id,
        attributes: {
          name: collection.name,
          description: collection.description,
          metadata: collection.metadata,
          createdAt: collection.createdAt,
          updatedAt: collection.updatedAt,
          documents: { data: [] } // Will be populated when documents are fetched
        }
      };
    } catch (error) {
      console.error('Error creating onboarding collection:', error);
      throw error;
    }
  }

  async getCollection(collectionId: string): Promise<Collection> {
    try {
      const response = await fetch(`${this.baseUrl}/collections/${collectionId}?populate=*`, {
        headers: authService.getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json() as StrapiError;
        throw new Error(error.error?.message || `Failed to fetch collection: ${response.statusText}`);
      }

      const collection = await response.json();
      
      // Transform to expected Collection format
      return {
        id: collection.id,
        attributes: {
          name: collection.name,
          description: collection.description,
          metadata: collection.metadata,
          createdAt: collection.createdAt,
          updatedAt: collection.updatedAt,
          documents: collection.documents || { data: [] }
        }
      };
    } catch (error) {
      console.error('Error fetching collection:', error);
      throw error;
    }
  }

  async getCollectionDocuments(collectionId: string): Promise<Document[]> {
    try {
      // Use the correct API endpoint: /collections/:id/documents
      const documentsUrl = `${this.baseUrl}/collections/${collectionId}/documents`;

      const response = await fetch(documentsUrl, {
        headers: authService.getAuthHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch collection documents: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const response_data = await response.json();
      
      // Handle API response format (might have data wrapper or be direct array)
      const documents = response_data.data || response_data;
      
      if (Array.isArray(documents)) {
        return documents.map((doc: any) => ({
          id: doc.id,
          attributes: {
            title: doc.title || doc.name,
            fileUrl: doc.fileUrl || doc.url,
            fileSize: doc.fileSize || doc.size,
            mimeType: doc.mimeType || doc.mime,
            extension: doc.extension,
            status: doc.status,
            metadata: doc.metadata,
            createdAt: doc.createdAt,
            updatedAt: doc.updatedAt
          }
        }));
      }
      
      // Return empty array if no documents
      return [];
    } catch (error) {
      console.error('Error fetching collection documents:', error);
      throw error;
    }
  }

  async updateCollectionMetadata(collectionId: string, metadata: any): Promise<Collection> {
    try {
      const response = await fetch(`${this.baseUrl}/collections/${collectionId}`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ 
          data: { 
            metadata: {
              ...metadata,
              updated_at: new Date().toISOString()
            }
          } 
        })
      });

      if (!response.ok) {
        const error = await response.json() as StrapiError;
        throw new Error(error.error?.message || `Failed to update collection: ${response.statusText}`);
      }

      const result = await response.json() as StrapiResponse<Collection>;
      return result.data;
    } catch (error) {
      console.error('Error updating collection metadata:', error);
      throw error;
    }
  }

  // Document Management
  async uploadDocument(collectionId: string, file: File, documentType: string): Promise<Document> {
    try {
      
      // Step 1: Upload document to get document ID
      const formData = new FormData();
      formData.append('file', file); // Single file
      formData.append('metadata', JSON.stringify({
        originalName: file.name,
        documentType: documentType,
        uploadedAt: new Date().toISOString()
      }));

      const documentUploadResponse = await fetch(`${this.baseUrl}/documents/upload`, {
        method: 'POST',
        headers: authService.getUploadHeaders(),
        body: formData
      });

      if (!documentUploadResponse.ok) {
        const error = await documentUploadResponse.text();
        throw new Error(`Failed to upload document: ${documentUploadResponse.status} ${documentUploadResponse.statusText} - ${error}`);
      }

      const uploadedDocument = await documentUploadResponse.json();

      if (!uploadedDocument || !uploadedDocument.id) {
        throw new Error('No document was created');
      }


      // Step 2: Add document (using document ID) to collection
      
      // Backend expects just the document ID (not wrapped in data)
      const addDocumentPayload = {
        documentId: uploadedDocument.id
      };

      const addToCollectionResponse = await fetch(`${this.baseUrl}/collections/${collectionId}/add-document`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(addDocumentPayload)
      });

      if (!addToCollectionResponse.ok) {
        const errorText = await addToCollectionResponse.text();
        throw new Error(`Failed to add document to collection: ${addToCollectionResponse.status} ${addToCollectionResponse.statusText} - ${errorText}`);
      }

      await addToCollectionResponse.json();
      console.log('✅ Document added to collection successfully');
      
      // Return the document in our expected format
      // Use data from the upload response since that has the document details
      return {
        id: uploadedDocument.id,
        attributes: {
          title: uploadedDocument.title || file.name,
          fileUrl: uploadedDocument.fileUrl,
          fileSize: uploadedDocument.fileSize || file.size,
          mimeType: uploadedDocument.mimeType || file.type,
          extension: uploadedDocument.extension || file.name.split('.').pop(),
          status: uploadedDocument.status || 'uploaded',
          metadata: uploadedDocument.metadata || {},
          uploadedAt: uploadedDocument.uploadedAt || new Date().toISOString(),
          updatedAt: uploadedDocument.updatedAt || new Date().toISOString()
        }
      };
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  }

  // Removed old getDocuments method - use getCollectionDocuments instead

  async updateDocumentMetadata(documentId: string, metadata: any): Promise<Document> {
    try {
      const response = await fetch(`${this.baseUrl}/documents/${documentId}`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ 
          data: { 
            inferredMetadata: metadata,
            status: 'processed',
            metadata: {
              processedAt: new Date().toISOString()
            }
          } 
        })
      });

      if (!response.ok) {
        const error = await response.json() as StrapiError;
        throw new Error(error.error?.message || `Failed to update document: ${response.statusText}`);
      }

      const result = await response.json() as StrapiResponse<Document>;
      return result.data;
    } catch (error) {
      console.error('Error updating document metadata:', error);
      throw error;
    }
  }

  async deleteDocument(documentId: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/documents/${documentId}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json() as StrapiError;
        throw new Error(error.error?.message || `Failed to delete document: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }

  // Document Types
  async getRequiredDocumentTypes(): Promise<DocumentType[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/document-types?filters[level][$eq]=system&populate=*`,
        {
          headers: authService.getAuthHeaders()
        }
      );

      if (!response.ok) {
        const error = await response.json() as StrapiError;
        throw new Error(error.error?.message || `Failed to fetch document types: ${response.statusText}`);
      }

      const result = await response.json() as StrapiResponse<DocumentType[]>;
      return result.data;
    } catch (error) {
      console.error('Error fetching document types:', error);
      throw error;
    }
  }

  async getDocumentTypeByName(documentTypeName: string): Promise<DocumentType | null> {
    try {
      const response = await fetch(
        `${this.baseUrl}/document-types?filters[name][$eq]=${documentTypeName}&populate[prompts]=*`,
        {
          headers: authService.getAuthHeaders()
        }
      );

      if (!response.ok) {
        const error = await response.json() as StrapiError;
        throw new Error(error.error?.message || `Failed to fetch document type: ${response.statusText}`);
      }

      const result = await response.json() as StrapiResponse<DocumentType[]>;
      return result.data[0] || null;
    } catch (error) {
      console.error('Error fetching document type:', error);
      throw error;
    }
  }

  // Helper Methods
  async checkConnection(): Promise<boolean> {
    try {
      // Test with a simple endpoint that should exist
      const testUrl = `${this.baseUrl}/collections`;
      console.log(`🌐 Testing DMS connection to: ${testUrl}`);
      
      const response = await fetch(testUrl, {
        method: 'GET',
        headers: authService.getAuthHeaders()
      });
      
      console.log(`📡 DMS response status: ${response.status} ${response.statusText}`);
      
      // For connection test, 200 is good, 405 might mean endpoint exists but method not allowed
      // Both indicate the service is running
      return response.status === 200 || response.status === 405;
    } catch (error) {
      console.error('❌ DMS connection check failed:', error);
      return false;
    }
  }

  async getCollectionDocumentCount(collectionId: string): Promise<number> {
    try {
      const documents = await this.getCollectionDocuments(collectionId);
      return documents.length;
    } catch (error) {
      console.error('Error getting document count:', error);
      return 0;
    }
  }

  async getCollectionByEmployeeId(employeeId: string): Promise<Collection | null> {
    try {
      const searchUrl = `${this.baseUrl}/collections?filters[name][$contains]=Employee_Onboarding_${employeeId}&populate=*`;
      console.log('🔍 Searching for existing collection for employee:', employeeId);
      
      const response = await fetch(searchUrl, {
        headers: authService.getAuthHeaders()
      });

      if (!response.ok) {
        console.log('❌ Collection search failed:', response.status, response.statusText);
        const error = await response.json() as StrapiError;
        throw new Error(error.error?.message || `Failed to fetch collection: ${response.statusText}`);
      }

      const response_data = await response.json();
      console.log('📋 Collections search response:', response_data);
      
      // Handle API response format with data wrapper
      const collections = response_data.data || response_data;
      
      if (Array.isArray(collections) && collections.length > 0) {
        const collection = collections[0];
        console.log('✅ Found existing collection:', collection.id, collection.name);
        // Transform to expected Collection format
        return {
          id: collection.id,
          attributes: {
            name: collection.name,
            description: collection.description,
            metadata: collection.metadata,
            createdAt: collection.createdAt,
            updatedAt: collection.updatedAt,
            documents: collection.documents ? { data: collection.documents } : { data: [] }
          }
        };
      }
      
      console.log('❌ No existing collections found for employee:', employeeId);
      return null;
    } catch (error) {
      console.error('Error fetching collection by employee ID:', error);
      throw error;
    }
  }
}

// Export a singleton instance
export const dmsService = new TurfAIDMSService();